import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';

const API = process.env.REACT_APP_BACKEND_URL;
const apiBase = `${API}/api`;

/**
 * Daily Digest Settings — toggle enable/disable, manage recipient list,
 * preview the next digest, and force-send a digest immediately.
 *
 * All persistence runs through /api/email/digest/settings (POST). The 'send-now'
 * button bypasses the once-per-day idempotence guard server-side.
 */
export default function DailyDigestSettings() {
  const [enabled, setEnabled] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${apiBase}/email/digest/settings`, { credentials: 'include' });
      const d = await r.json();
      if (r.ok) {
        setEnabled(!!d.enabled);
        setRecipients(d.recipients || []);
      }
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const persist = async (next) => {
    setSaving(true);
    try {
      const r = await fetch(`${apiBase}/email/digest/settings`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'opslaan mislukt');
      setEnabled(!!d.enabled);
      setRecipients(d.recipients || []);
      toast.success('Opgeslagen');
    } catch (e) {
      toast.error(e.message || 'Opslaan mislukt');
    }
    setSaving(false);
  };

  const toggleEnabled = (v) => {
    setEnabled(v);
    persist({ enabled: v, recipients });
  };

  const addRecipient = () => {
    const e = (newEmail || '').trim().toLowerCase();
    if (!e || !e.includes('@')) {
      toast.error('Ongeldig e-mailadres');
      return;
    }
    if (recipients.includes(e)) {
      toast.error('Adres staat al in de lijst');
      return;
    }
    const next = [...recipients, e];
    setRecipients(next);
    setNewEmail('');
    persist({ enabled, recipients: next });
  };

  const removeRecipient = (e) => {
    const next = recipients.filter((x) => x !== e);
    setRecipients(next);
    persist({ enabled, recipients: next });
  };

  const openPreview = async () => {
    setShowPreview(true);
    setPreviewLoading(true);
    try {
      const r = await fetch(`${apiBase}/email/digest/preview`, { credentials: 'include' });
      const d = await r.json();
      if (r.ok) setPreviewHtml(d.html || '');
    } catch {
      toast.error('Preview kon niet geladen worden');
    }
    setPreviewLoading(false);
  };

  const sendNow = async () => {
    if (!enabled || recipients.length === 0) {
      toast.error('Schakel eerst de digest in en voeg minstens één ontvanger toe');
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${apiBase}/email/digest/send-now`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      const d = await r.json();
      if (d.ok) {
        toast.success(`Digest verzonden naar ${(d.sent_to || []).length} ontvanger(s)`);
      } else {
        toast.error(d.reason === 'no_recipients' ? 'Geen ontvangers ingesteld' :
                    d.reason === 'digest_disabled' ? 'Digest staat uit' :
                    `Send mislukt${d.errors?.length ? `: ${d.errors[0].error}` : ''}`);
      }
    } catch (e) {
      toast.error(e.message || 'Send mislukt');
    }
    setSending(false);
  };

  if (loading) {
    return <div className="border border-zinc-200 rounded-md p-4 text-sm text-zinc-500">Digest-instellingen laden...</div>;
  }

  return (
    <div className="border border-zinc-200 rounded-md bg-white p-5 mb-4" data-testid="daily-digest-settings">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1">
          <h3 className="font-heading text-base font-semibold flex items-center gap-2">
            💌 Daily Digest
          </h3>
          <p className="text-xs text-zinc-500 mt-1">
            Eén keer per dag (08:00 NL) krijg je een samenvatting per profile: Sequence Health Scores,
            top movers van de afgelopen 24h, automatische rewrites, geadopteerde A/B winnaars
            en de Predictor Calibration trend.
          </p>
        </div>
        <Switch checked={enabled} onCheckedChange={toggleEnabled} disabled={saving}
          data-testid="digest-enabled-switch" />
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-600 block mb-1.5">
            Ontvangers ({recipients.length})
          </label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {recipients.length === 0 ? (
              <span className="text-[11px] text-zinc-400 italic">Nog geen ontvangers — voeg minstens één e-mail toe</span>
            ) : recipients.map((r) => (
              <span key={r} className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-[11px] text-emerald-800"
                    data-testid={`digest-recipient-${r}`}>
                {r}
                <button onClick={() => removeRecipient(r)} className="ml-1 text-emerald-600 hover:text-rose-600"
                        aria-label={`Verwijder ${r}`} data-testid={`digest-recipient-remove-${r}`}>×</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              type="email" placeholder="voorbeeld@bedrijf.nl"
              value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }}
              className="text-sm flex-1" data-testid="digest-new-recipient-input"
            />
            <Button onClick={addRecipient} size="sm" disabled={saving || !newEmail.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="digest-add-recipient-btn">
              Toevoegen
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-100">
          <Button onClick={openPreview} size="sm" variant="outline"
                  className="border-sky-300 text-sky-700 hover:bg-sky-50"
                  data-testid="digest-preview-btn">
            👁 Bekijk preview
          </Button>
          <Button onClick={sendNow} size="sm" disabled={sending || !enabled || recipients.length === 0}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="digest-send-now-btn">
            {sending ? 'Verzenden...' : '📨 Verstuur nu'}
          </Button>
        </div>
      </div>

      {showPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-white rounded-md shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="digest-preview-modal">
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
              <h4 className="font-heading text-sm font-semibold">Digest Preview</h4>
              <button onClick={() => setShowPreview(false)} className="text-zinc-400 hover:text-zinc-700 text-xl leading-none" data-testid="digest-preview-close">×</button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {previewLoading ? (
                <div className="p-6 text-sm text-zinc-500 italic">Preview laden...</div>
              ) : (
                <iframe srcDoc={previewHtml} title="digest preview" className="w-full h-[600px] border-0" data-testid="digest-preview-iframe" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
